import { ArrowRightOutlined, SignatureFilled } from '@ant-design/icons';
import {
  RichiestaDTO,
  getGetFrontRichiesteIdModuliIdQueryKey,
  getGetFrontRichiesteIdModuliQueryKey,
  usePostFrontRichiesteIdModuloIdDocumentoFirma,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  DownloadSubmissionModuleButton,
  IFrontOfficeModule,
  SubmissionLogic,
} from '@framework/frontoffice-submission';
import { Trans, useTranslation } from '@framework/i18n';
import { AsyncButton } from '@framework/ui';
import { Flex, Space, Tooltip, Typography, UploadFile, message } from 'antd';
import clx from 'classnames';
import { lighten } from 'polished';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { queryClient } from '../../../../core/network/queryClient';
import { SignedModuleFileRow } from './SignedModuleFileRow';
import {
  UploadSignedModuleInput,
  getFileItem,
} from './UploadSignedModuleInput';
const { Text } = Typography;

const ModuleSignerWrapper = styled.div`
  padding: 12px;
  /* background-color: ${props => props.theme.colorSplit}; */
  border-radius: ${props => props.theme.borderRadius}px;
  border: 1px solid ${props => lighten(0.35, props.theme.moduleDelegateColor)};
  margin-bottom: 12px;
  .ant-btn-default {
    border-color: ${props => props.theme.moduleDelegateColor};
  }
`;
const ModuleSignerIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  font-size: 24px;
  line-height: 24px;
  padding: 0 6px;
  color: ${props => lighten(0.4, props.theme.moduleDelegateColor)};
  background-color: ${props => lighten(0.1, props.theme.moduleDelegateColor)};
  &.is-mobile {
    display: block;
    width: 100%;
    height: 4px;
  }
`;
const ModuleSignerTitle = styled.div`
  margin-top: -4px;
  margin-bottom: 4px;
`;
const ActionMessageText = styled(Text)`
  font-size: 16px;
  line-height: 1.4;
  .anticon {
    font-size: 24px;
    color: ${props => props.theme.moduleDelegateColor};
  }
`;

const ModuleSignerSpace = styled(Space.Compact)`
  /* margin-bottom: 24px; */
`;

export interface ISubmissionModuleSignerProps {
  submission?: RichiestaDTO | null;
  submissionModule?: IFrontOfficeModule | null;
  showTitle?: boolean;
}

export function SubmissionModuleSigner(props: ISubmissionModuleSignerProps) {
  const { submission, submissionModule, showTitle } = props;
  const { t } = useTranslation();

  const submissionId = submission?.id;
  const submissionModuleId = submissionModule?.moduloRichiestaId;
  const disabled =
    SubmissionLogic.isSubmissionNew(submission) ||
    !submissionModule?.statoValidazione?.visitato;

  const [file, setFile] = useState<UploadFile | null>(
    submissionModule?.idFileFirmato
      ? getFileItem(submissionModule.idFileFirmato)
      : null,
  );

  const isMobile = useBreakpoint(down('md'));

  const upload = usePostFrontRichiesteIdModuloIdDocumentoFirma();

  const handleClick = useCallback(async () => {
    if (!file) {
      console.error('[Upload] Error: file not found');
      return;
    }

    if (!submissionId || !submissionModuleId) {
      return;
    }

    // NB Per qualche motivo la postFrontRichiesteIdModuloIdDocumentoFirma non converte l'input in formData
    const data = new FormData();
    data.append('file', file as any);

    try {
      const submissionModule = await upload.mutateAsync({
        idRichiesta: submissionId,
        idRichiestaModulo: submissionModuleId,
        data: data as any,
      });

      const fileId = submissionModule.idFileFirmato;
      if (!fileId) {
        console.error('[Upload] Error: file not found in response', {
          submissionModule,
        });
        message.error(
          t(
            'richieste.firma.messaggio-errore-caricamento-file',
            'Errore durante il caricamento del file, riprovare',
          ),
        );
        return;
      }

      setFile(getFileItem(fileId));

      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdModuliIdQueryKey(
          submissionId,
          submissionModuleId,
        ),
      );

      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdModuliQueryKey(submissionId),
      );

      message.success(
        t(
          'richieste.firma.messaggio-successo-caricamento-file',
          'Il modulo firmato è stato caricato correttamente.',
        ),
      );
    } catch (err) {
      console.error('[Upload] Error after API', err);
      message.error(getNetworkErrorMessage(err, t('richieste.firma.messaggio-errore-caricamento-file','Errore durante il caricamento del file, riprovare'))); // prettier-ignore
    }
  }, [file, submissionId, submissionModuleId, t, upload]);

  if (SubmissionLogic.isSubmissionSent(submission)) {
    return null;
  }

  return (
    <>
      <ModuleSignerWrapper>
        <Flex vertical gap={8}>
          {showTitle && (
            <ModuleSignerTitle>
              <ActionMessageText strong>
                <Space>
                  <SignatureFilled aria-hidden="true" />
                  <span>
                    {t(
                      'richieste.firma.procedura-firma-modulo',
                      'Procedura di Firma Modulo',
                    )}
                  </span>
                </Space>
              </ActionMessageText>
            </ModuleSignerTitle>
          )}
          <ModuleSignerSpace
            block
            direction={isMobile ? 'vertical' : 'horizontal'}
          >
            <DownloadSubmissionModuleButton
              disabled={disabled}
              submissionId={submissionId}
              submissionModuleId={submissionModuleId}
              ignoreMobile
              forceIcon
              // Scarichiamo sempre quello generato
              downloadType="generato"
            />

            <ModuleSignerIcon className={clx({ 'is-mobile': isMobile })}>
              {!isMobile && <ArrowRightOutlined aria-hidden />}
            </ModuleSignerIcon>

            <UploadSignedModuleInput
              disabled={disabled}
              submissionModuleId={submissionModuleId}
              setFile={setFile}
              loading={upload.isLoading}
            />
            <Tooltip
              title={
                !file
                  ? t(
                      'richieste.firma.carica-documento-firmato-per-continuare',
                      'Carica un documento firmato per continuare',
                    )
                  : undefined
              }
            >
              <AsyncButton
                size="large"
                type="primary"
                onClick={handleClick}
                disabled={!file}
              >
                {t('richieste.firma.carica-e-completa', 'Carica e Completa')}
              </AsyncButton>
            </Tooltip>
          </ModuleSignerSpace>
          <SignedModuleFileRow
            file={file}
            submissionId={submissionId}
            submissionModuleId={submissionModuleId}
          />
          {showTitle && (
            <Text>
              <Trans
                i18nKey="richieste.firma.scaricare-il-modulo-per-procedere-alla-firma"
                defaults="Scaricare il modulo <title/>, procedere alla firma digitale e ricaricare il modulo firmato."
                components={{
                  title: <Text strong>{submissionModule?.titolo}</Text>,
                }}
              />
            </Text>
          )}
        </Flex>
      </ModuleSignerWrapper>
    </>
  );
}
